import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { fetchNavigation, type Navigation } from '../../services/navigation';
import { PAGE_SECTIONS } from './homeSections';

interface DropdownItem {
  label: string;
  path: string;
  sectionId?: string;
}

interface NavItem {
  label: string;
  path: string;
  isButton?: boolean;
  sectionId?: string;
  children?: DropdownItem[];
}

function buildNavStructure(nav: Navigation): NavItem[] {
  const topLevel: NavItem[] = [];

  for (const item of nav.menuItems) {
    if (item.isButton) {
      topLevel.push({ label: item.label, path: item.path, isButton: true });
      continue;
    }

    // Hash-link items (home-page sections) are surfaced via the right-side SectionRailNav,
    // not the top navbar - keeps the top nav clean and avoids the burgundy stripe overlap.
    if (item.path.startsWith('/#')) continue;

    topLevel.push({ label: item.label, path: item.path });
  }

  const result: NavItem[] = [];
  result.push({ label: 'BHS-Modell', path: '/bhs-modell' });

  const regular = topLevel.filter((i) => i.path !== '/bhs-modell' && !i.isButton);
  const buttons = topLevel.filter((i) => i.path !== '/bhs-modell' && i.isButton);

  result.push(...regular);

  // Blog page exists but isn't always in the Strapi nav - hardcode it like BHS-Modell.
  if (!result.some((r) => r.path === '/blog')) {
    result.push({ label: 'Blog', path: '/blog' });
  }

  result.push(...buttons);

  return result;
}

export default function Navbar() {
  const [navigation, setNavigation] = useState<Navigation | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [forceWhite, setForceWhite] = useState(false);
  const [homeLoaded, setHomeLoaded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState<string | null>(null);
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout>>();
  const isNavClickingRef = useRef(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  // Pages with a full-bleed hero the navbar goes transparent over (currently just home).
  const hasHeroBanner = isHome;
  const currentPageSections = PAGE_SECTIONS[location.pathname] || [];
  const hasSections = currentPageSections.length > 0;

  // Close the desktop "Sektionen" dropdown on outside click or Escape
  useEffect(() => {
    if (!sectionsOpen) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-sections-dropdown]')) {
        setSectionsOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSectionsOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [sectionsOpen]);

  useEffect(() => {
    const loadNavigation = async () => {
      try {
        const data = await fetchNavigation();
        setNavigation(data);
      } catch (error) {
        console.error('Failed to load navigation:', error);
      }
    };
    loadNavigation();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      setActiveSection((e as CustomEvent).detail || '');
    };
    window.addEventListener('navbar:activeSection', handler);
    return () => window.removeEventListener('navbar:activeSection', handler);
  }, []);

  useEffect(() => {
    const handler = () => setHomeLoaded(true);
    window.addEventListener('home:loaded', handler);
    return () => window.removeEventListener('home:loaded', handler);
  }, []);

  useEffect(() => {
    if (!hasHeroBanner) {
      setHomeLoaded(false);
    }
    setActiveSection('');
  }, [hasHeroBanner, location.pathname]);

  // Page-agnostic scroll spy: observes whatever sections the current path has registered
  // in PAGE_SECTIONS. Polls briefly so it picks up sections that mount after the page's
  // data fetch resolves.
  useEffect(() => {
    if (!hasSections) return;

    let observer: IntersectionObserver | null = null;
    let pollInterval: ReturnType<typeof setInterval> | null = null;

    const setup = () => {
      const elements = currentPageSections
        .map((s) => document.getElementById(s.sectionId))
        .filter((el): el is HTMLElement => el !== null);

      if (elements.length === 0) return false;

      observer?.disconnect();
      observer = new IntersectionObserver(
        (entries) => {
          if (isNavClickingRef.current) return;
          const visible = entries.find((e) => e.isIntersecting);
          if (visible) setActiveSection(visible.target.id);
        },
        { rootMargin: '-40% 0px -50% 0px' },
      );
      elements.forEach((el) => observer!.observe(el));
      return elements.length >= currentPageSections.length;
    };

    const allReady = setup();
    if (!allReady) {
      let attempts = 0;
      pollInterval = setInterval(() => {
        attempts++;
        if (setup() || attempts > 30) {
          if (pollInterval) clearInterval(pollInterval);
        }
      }, 200);
    }

    return () => {
      observer?.disconnect();
      if (pollInterval) clearInterval(pollInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
    setMobileSubmenuOpen(null);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (isHome && location.hash) {
      setForceWhite(true);
    } else if (!location.hash) {
      setForceWhite(false);
    }
  }, [isHome, location.hash]);

  useEffect(() => {
    if (forceWhite && scrolled) {
      setForceWhite(false);
    }
  }, [forceWhite, scrolled]);

  if (!navigation) {
    return (
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white" style={{ boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20" />
        </div>
      </nav>
    );
  }

  const navItems = buildNavStructure(navigation);

  const handleNavClick = (sectionId: string) => {
    if (!sectionId) return;
    isNavClickingRef.current = true;
    setActiveSection(sectionId);
    if (isHome) setForceWhite(true);
    window.dispatchEvent(new CustomEvent('navbar:navClick', { detail: sectionId }));
    setTimeout(() => {
      isNavClickingRef.current = false;
    }, 1000);
  };

  const handleLogoClick = () => {
    if (isHome) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      window.history.replaceState(null, '', '/');
    }
  };

  const handleDropdownEnter = (label: string) => {
    clearTimeout(dropdownTimeout.current);
    setOpenDropdown(label);
  };

  const handleDropdownLeave = () => {
    dropdownTimeout.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  const renderDesktopNav = (textColor: 'light' | 'dark') => {
    const isLight = textColor === 'light';
    return (
      <div className="hidden lg:flex items-center space-x-1">
        {navItems.map((item) => {
          if (item.isButton) {
            return (
              <Link
                key={item.label}
                to={item.path}
                className="ml-3 px-6 py-2.5 bg-[#8B1538] text-white rounded-full text-sm font-medium hover:bg-[#6B0F2A] transition-all cursor-pointer whitespace-nowrap"
              >
                {item.label}
              </Link>
            );
          }

          if (item.children) {
            const isOpen = openDropdown === item.label;
            const hasActiveChild = item.children.some((c) => c.sectionId && activeSection === c.sectionId);
            const isActive = (isHome && hasActiveChild) || (isHome && !activeSection && item.path === '/');

            return (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => handleDropdownEnter(item.label)}
                onMouseLeave={handleDropdownLeave}
              >
                <Link
                  to={item.path}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    isLight
                      ? isActive ? 'text-[#D4AF37]' : 'text-white hover:text-white/80'
                      : isActive ? 'text-[#1A2B3C]' : 'text-[#1A2B3C] hover:text-[#8B1538]'
                  }`}
                >
                  <span
                    className={`border-b-2 pb-0.5 transition-colors ${
                      isActive
                        ? isLight ? 'border-[#D4AF37] font-semibold' : 'border-[#8B1538] font-semibold'
                        : 'border-transparent font-medium'
                    }`}
                  >
                    {item.label}
                  </span>
                  <i className={`ri-arrow-down-s-line text-sm opacity-70 transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
                </Link>

                <div
                  className={`absolute left-0 top-full pt-2 transition-all duration-200 ${
                    isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1'
                  }`}
                >
                  <div className="w-56 rounded-xl bg-white shadow-xl shadow-black/8 border border-gray-100 py-2 overflow-hidden">
                    {item.children.map((child) => {
                      const childActive = isHome && child.sectionId && activeSection === child.sectionId;
                      return isHome && child.sectionId ? (
                        <a
                          key={child.label}
                          href={child.path}
                          onClick={() => {
                            handleNavClick(child.sectionId!);
                            setOpenDropdown(null);
                          }}
                          className={`block px-4 py-2.5 text-sm transition-colors ${
                            childActive
                              ? 'text-[#8B1538] bg-[#8B1538]/5 font-medium'
                              : 'text-[#1A2B3C] hover:bg-gray-50 hover:text-[#8B1538]'
                          }`}
                        >
                          {child.label}
                        </a>
                      ) : (
                        <Link
                          key={child.label}
                          to={child.path}
                          onClick={() => setOpenDropdown(null)}
                          className="block px-4 py-2.5 text-sm text-[#1A2B3C] hover:bg-gray-50 hover:text-[#8B1538] transition-colors"
                        >
                          {child.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          }

          const isPageActive = location.pathname === item.path;
          return (
            <Link
              key={item.label}
              to={item.path}
              className={`inline-block px-4 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                isLight
                  ? isPageActive ? 'text-[#D4AF37]' : 'text-white hover:text-white/80'
                  : isPageActive ? 'text-[#1A2B3C]' : 'text-[#1A2B3C] hover:text-[#8B1538]'
              }`}
            >
              <span
                className={`border-b-2 pb-0.5 transition-colors ${
                  isPageActive
                    ? isLight ? 'border-[#D4AF37] font-semibold' : 'border-[#8B1538] font-semibold'
                    : 'border-transparent font-medium'
                }`}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    );
  };

  const renderNavContent = (textColor: 'light' | 'dark') => (
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="flex items-center justify-between h-20">
        <div className="flex items-center gap-4">
          <Link to="/" onClick={handleLogoClick} className="flex items-center space-x-3">
            {navigation.logoUrl ? (
              <img
                src={navigation.logoUrl}
                alt={navigation.logoText ? `${navigation.logoText} Logo` : 'Gerald Fasching Logo'}
                width={64}
                height={64}
                className="h-16 w-auto"
              />
            ) : (
              <div className="w-16 h-16 flex items-center justify-center">
                <i className={`ri-fire-line text-4xl ${textColor === 'light' ? 'text-[#D4AF37]' : 'text-[#8B1538]'}`}></i>
              </div>
            )}
            {navigation.showLogoText && navigation.logoText && (
              <span
                className="text-xl font-bold"
                style={{ color: textColor === 'light' ? '#FFFFFF' : (navigation.logoTextColor || '#1A2B3C') }}
              >
                {navigation.logoText}
              </span>
            )}
          </Link>
          {/* "Sektionen" dropdown - desktop only, on any page that has registered sections. Pill is white on every variant (matches the scrolled navbar) so it stays readable on both the navy hero half and the cream side. */}
          {hasSections && (
            <div className="relative hidden lg:block" data-sections-dropdown>
              <button
                type="button"
                onClick={() => setSectionsOpen((o) => !o)}
                aria-expanded={sectionsOpen}
                aria-haspopup="menu"
                className="inline-flex items-center gap-1.5 rounded-full border border-[#1A2B3C]/15 bg-white px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-[#1A2B3C] shadow-sm transition-colors hover:border-[#8B1538]/30 hover:text-[#8B1538] lg:text-[13px]"
              >
                {navigation.sectionsLabel || 'Sektionen'}
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${sectionsOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {/* Panel renders in both nav-content variants but the light-variant copy gets clipped by the diagonal clip-path on its parent layer - so visually only one panel shows. */}
              <div
                role="menu"
                className={`absolute left-0 top-full z-50 mt-3 w-64 rounded-xl bg-white p-2 shadow-[0_18px_48px_-12px_rgba(26,43,60,0.32)] ring-1 ring-[#1A2B3C]/[0.06] transition-all duration-200 ${
                  sectionsOpen ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-1 opacity-0'
                }`}
              >
                <p className="px-3 py-2 text-xs font-bold uppercase tracking-[0.22em] text-[#1A2B3C]/45 lg:text-[13px]">
                  {navigation.onThisPageLabel || 'Auf dieser Seite'}
                </p>
                {currentPageSections.map((section) => {
                  const isSectionActive = activeSection === section.sectionId;
                  return (
                    <a
                      key={section.sectionId}
                      href={`#${section.sectionId}`}
                      role="menuitem"
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavClick(section.sectionId);
                        setSectionsOpen(false);
                        const el = document.getElementById(section.sectionId);
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                        window.history.pushState(null, '', `#${section.sectionId}`);
                      }}
                      className={`block rounded-md px-3 py-2 text-sm transition-colors ${
                        isSectionActive
                          ? 'bg-[#8B1538]/10 font-semibold text-[#8B1538]'
                          : 'text-[#1A2B3C] hover:bg-[#1A2B3C]/[0.04] hover:text-[#8B1538]'
                      }`}
                    >
                      {section.label}
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        {renderDesktopNav(textColor)}
        <button
          className="lg:hidden w-8 h-8 flex items-center justify-center"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${textColor === 'light' ? 'text-white' : 'text-[#1A2B3C]'}`}></i>
        </button>
      </div>
    </div>
  );

  const showTransparent = hasHeroBanner && homeLoaded && !scrolled && !forceWhite;

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        backgroundColor: showTransparent ? 'transparent' : '#ffffff',
        boxShadow: showTransparent ? 'none' : '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
      }}
    >
      <div className="relative">
        {renderNavContent('dark')}
        {/* White-text overlay, shown while the navbar sits transparent over a hero. It spans the
             full width: the hero is dark edge to edge (navy wedge on the left, dimmed office photo
             on the right), so the dark-text variant underneath would be unreadable anywhere. This
             used to be clipped to the old hero's 60% diagonal, back when the right half was cream. */}
        <div
          className={`absolute inset-0 ${forceWhite ? '' : 'transition-opacity duration-500'} ${showTransparent ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          {renderNavContent('light')}
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 relative z-10 ${
          showTransparent ? 'bg-[#1A365D]' : 'bg-white'
        } ${mobileMenuOpen ? 'max-h-[80vh] overflow-y-auto shadow-lg' : 'max-h-0'}`}
      >
        <div className="px-6 py-4 pb-6 space-y-1">
          {/* Section anchors for the current page (fezle.io style: SECTIONS group at the top of the burger) */}
          {hasSections && (
            <>
              <p className={`px-4 pt-2 pb-2 text-xs font-bold uppercase tracking-[0.25em] lg:text-[13px] ${
                showTransparent ? 'text-white/50' : 'text-[#1A2B3C]/45'
              }`}>
                {navigation.sectionsLabel || 'Sektionen'}
              </p>
              {currentPageSections.map((section) => {
                const sectionActive = activeSection === section.sectionId;
                return (
                  <a
                    key={section.sectionId}
                    href={`#${section.sectionId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(section.sectionId);
                      setMobileMenuOpen(false);
                      const el = document.getElementById(section.sectionId);
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                      window.history.pushState(null, '', `#${section.sectionId}`);
                    }}
                    className={`block px-4 py-2.5 text-sm rounded-lg transition-colors ${
                      showTransparent
                        ? sectionActive ? 'text-[#D4AF37] font-semibold' : 'text-white/85 hover:bg-white/10'
                        : sectionActive ? 'text-[#8B1538] font-semibold bg-[#8B1538]/8' : 'text-[#1A2B3C]/80 hover:bg-gray-50'
                    }`}
                  >
                    {section.label}
                  </a>
                );
              })}
              <div className={`my-3 border-t ${showTransparent ? 'border-white/15' : 'border-[#1A2B3C]/10'}`} />
            </>
          )}

          {navItems.map((item) => {
            if (item.isButton) {
              return (
                <Link
                  key={item.label}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-3 bg-[#8B1538] text-white rounded-full text-sm font-medium text-center mt-3"
                >
                  {item.label}
                </Link>
              );
            }

            if (item.children) {
              const isSubmenuOpen = mobileSubmenuOpen === item.label;
              return (
                <div key={item.label}>
                  <button
                    onClick={() => setMobileSubmenuOpen(isSubmenuOpen ? null : item.label)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                      showTransparent ? 'text-white hover:bg-white/10' : 'text-[#1A2B3C] hover:bg-gray-50'
                    }`}
                  >
                    {item.label}
                    <i className={`ri-arrow-down-s-line text-xs transition-transform ${isSubmenuOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  <div className={`overflow-hidden transition-all duration-200 ${isSubmenuOpen ? 'max-h-96' : 'max-h-0'}`}>
                    <div className="pl-4 py-1 space-y-0.5">
                      {item.children.map((child) => {
                        const childActive = isHome && child.sectionId && activeSection === child.sectionId;
                        return isHome && child.sectionId ? (
                          <a
                            key={child.label}
                            href={child.path}
                            onClick={() => {
                              handleNavClick(child.sectionId!);
                              setMobileMenuOpen(false);
                            }}
                            className={`block px-4 py-2.5 text-sm rounded-lg transition-colors ${
                              showTransparent
                                ? childActive ? 'text-[#D4AF37]' : 'text-white/80 hover:bg-white/10'
                                : childActive ? 'text-[#8B1538] bg-red-50' : 'text-[#1A2B3C]/70 hover:bg-gray-50'
                            }`}
                          >
                            {child.label}
                          </a>
                        ) : (
                          <Link
                            key={child.label}
                            to={child.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`block px-4 py-2.5 text-sm rounded-lg transition-colors ${
                              showTransparent
                                ? 'text-white/80 hover:bg-white/10'
                                : 'text-[#1A2B3C]/70 hover:bg-gray-50'
                            }`}
                          >
                            {child.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }

            const isPageActive = location.pathname === item.path;
            return (
              <Link
                key={item.label}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  showTransparent
                    ? isPageActive ? 'text-[#D4AF37]' : 'text-white hover:bg-white/10'
                    : isPageActive ? 'text-[#8B1538] bg-red-50' : 'text-[#1A2B3C] hover:bg-gray-50'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>

      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[-1]"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </nav>
  );
}
